import {
  Badge,
  Box,
  Button as ButtonBase,
  Flex,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FiPaperclip } from 'react-icons/fi';
import { dataIsoParaBr } from '../../helpers/dataBr';
import { AnexoCirurgia } from '../../service/apontamentos';
import {
  CirurgiaDoPedido as ICirurgiaDoPedido,
  baixarAnexoDoAgendamento,
  buscarCirurgiaDoPedido,
  listarAnexosDoAgendamento,
  rotuloTipoCirurgia,
} from '../../service/cirurgiaDoPedido';

// Cast para evitar o TS2590 ("union type too complex") do Chakra.
const Button = ButtonBase as React.ComponentType<any>;

// Texto usa a ponta baixa da escala gray.* (gray.50 principal, gray.400
// secundário): o tema claro inverte os tokens e gray.900 some no fundo branco.
const Campo = ({ rotulo, valor }: { rotulo: string; valor?: string | null }) => (
  <Box minW={0}>
    <Text fontSize="10px" color="gray.400" fontWeight="700" letterSpacing="wider">
      {rotulo}
    </Text>
    <Text fontSize="sm" fontWeight="600" color="gray.50">
      {valor || '—'}
    </Text>
  </Box>
);

function localDoHospital(pedido: any): string {
  const endereco = pedido?.clientes?.endereco;
  return [endereco?.cidade, endereco?.uf].filter(Boolean).join('/');
}

export interface EstadoCirurgia {
  cirurgia: ICirurgiaDoPedido | null;
  anexos: AnexoCirurgia[];
  carregando: boolean;
  falhou: boolean;
}

/**
 * Busca o agendamento do pedido uma vez só para a tela toda: o card abaixo e
 * a impressão da folha de separação usam os mesmos dados. Pedido que não é de
 * URGÊNCIA/ELETIVA resolve em null sem chamar a API.
 */
export function useCirurgiaDoPedido(pedido: any): EstadoCirurgia {
  const [cirurgia, setCirurgia] = useState<ICirurgiaDoPedido | null>(null);
  const [anexos, setAnexos] = useState<AnexoCirurgia[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState(false);

  const pedidoId = pedido?.pedido_id;
  const colaboradorId = pedido?.colaborador_id;
  const tipoPedido = pedido?.tipo_pedido;

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setFalhou(false);
    setAnexos([]);

    buscarCirurgiaDoPedido({
      pedido_id: pedidoId,
      colaborador_id: colaboradorId,
      tipo_pedido: tipoPedido,
    })
      .then(async (encontrada) => {
        if (!ativo) return;
        setCirurgia(encontrada);
        if (!encontrada?.agendamentoId) return;
        // Anexo é apoio: se a listagem falhar, os dados da cirurgia continuam.
        const lista = await listarAnexosDoAgendamento(encontrada.agendamentoId).catch(() => []);
        if (ativo) setAnexos(lista);
      })
      .catch(() => {
        if (ativo) setFalhou(true);
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => {
      ativo = false;
    };
  }, [pedidoId, colaboradorId, tipoPedido]);

  return { cirurgia, anexos, carregando, falhou };
}

/**
 * Bloco com o que o vendedor preencheu no agendamento (data, hora, hospital,
 * paciente, médico, convênio, observações e documentos anexados). Só aparece
 * em pedido de URGÊNCIA/ELETIVA — é o que a logística precisa para separar o
 * material, inclusive quando o pedido travou em ERRO_INTEGRACAO.
 */
export function CirurgiaDoPedido({ pedido, estado }: { pedido: any; estado: EstadoCirurgia }) {
  const toast = useToast();
  const { cirurgia, anexos, carregando, falhou } = estado;
  const [abrindoAnexo, setAbrindoAnexo] = useState('');
  const tipoPedido = pedido?.tipo_pedido;

  async function abrirAnexo(anexo: AnexoCirurgia) {
    if (!cirurgia) return;
    // Abre a aba ANTES do await: depois dele o navegador já não considera um
    // clique do usuário e bloqueia o pop-up.
    const aba = window.open('', '_blank');
    setAbrindoAnexo(anexo.cirurgia_anexo_id);
    try {
      const blob = await baixarAnexoDoAgendamento(
        cirurgia.agendamentoId,
        anexo.cirurgia_anexo_id
      );
      const url = URL.createObjectURL(blob);
      if (aba) {
        aba.location.href = url;
      } else {
        window.location.assign(url);
      }
      // A aba já carregou o arquivo; libera a memória depois de um tempo.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      aba?.close();
      toast({
        title: 'ANEXO INDISPONÍVEL',
        description: 'Não foi possível abrir este documento.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setAbrindoAnexo('');
    }
  }

  const urgencia = String(cirurgia?.tipo || tipoPedido || '').toUpperCase() === 'URGENCIA';
  const hospital = cirurgia?.hospital || pedido?.clientes?.fantasia || '';
  const cidade = localDoHospital(pedido);
  const vendedor = pedido?.colaboradores?.nome || '';
  const feitoPor =
    pedido?.criado_por_nome && pedido?.criado_por_id !== pedido?.colaborador_id
      ? pedido.criado_por_nome
      : '';

  return (
    <VStack
      alignItems="stretch"
      spacing={4}
      mb={6}
      p={4}
      border="1px solid"
      borderColor={urgencia ? 'red.400' : 'blue.400'}
      borderRadius="md"
      bg="gray.900"
    >
      <Flex align="center" justify="space-between">
        <Text fontSize="14px" fontWeight="bold" color="gray.50">
          DADOS DA CIRURGIA
        </Text>
        <Badge colorScheme={urgencia ? 'red' : 'blue'} fontSize="12px" px={2}>
          {rotuloTipoCirurgia(cirurgia?.tipo || tipoPedido)}
        </Badge>
      </Flex>

      {carregando ? (
        <Flex justify="center" py={2}>
          <Spinner size="sm" color="gray.400" />
        </Flex>
      ) : falhou ? (
        <Text fontSize="sm" color="orange.200">
          Não foi possível carregar os dados da cirurgia. Recarregue a página.
        </Text>
      ) : !cirurgia ? (
        <Text fontSize="sm" color="orange.200">
          O agendamento deste pedido não foi encontrado.
        </Text>
      ) : (
        <>
          <SimpleGrid columns={{ base: 1, md: 3, xl: 4 }} spacing={4}>
            <Campo rotulo="DATA DA CIRURGIA" valor={dataIsoParaBr(cirurgia.data)} />
            <Campo rotulo="HORA" valor={cirurgia.hora} />
            <Campo
              rotulo="HOSPITAL"
              valor={cidade ? `${hospital} (${cidade})` : hospital}
            />
            <Campo rotulo="PACIENTE" valor={cirurgia.paciente} />
            <Campo
              rotulo="MÉDICO(A)"
              valor={
                cirurgia.medicoCrm
                  ? `${cirurgia.medico} — CRM ${cirurgia.medicoCrm}`
                  : cirurgia.medico
              }
            />
            <Campo rotulo="CONVÊNIO" valor={cirurgia.convenio} />
            <Campo rotulo="TIPO DE CIRURGIA" valor={pedido?.tipo_cirurgia} />
            <Campo rotulo="VENDEDOR(A)" valor={vendedor} />
            {feitoPor && <Campo rotulo="FEITO POR" valor={feitoPor} />}
            {pedido?.pedido_erp && <Campo rotulo="Nº PEDIDO TOTVS" valor={pedido.pedido_erp} />}
          </SimpleGrid>

          <Box>
            <Text fontSize="10px" color="gray.400" fontWeight="700" letterSpacing="wider">
              OBSERVAÇÕES DO VENDEDOR
            </Text>
            <Text fontSize="sm" color="gray.50" whiteSpace="pre-wrap">
              {cirurgia.observacoes || '—'}
            </Text>
          </Box>

          {anexos.length > 0 && (
            <Box>
              <Text
                fontSize="10px"
                color="gray.400"
                fontWeight="700"
                letterSpacing="wider"
                mb={2}
              >
                DOCUMENTOS ANEXADOS
              </Text>
              <Flex wrap="wrap" gridGap={2}>
                {anexos.map((anexo) => (
                  <Button
                    key={anexo.cirurgia_anexo_id}
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    leftIcon={<FiPaperclip />}
                    isLoading={abrindoAnexo === anexo.cirurgia_anexo_id}
                    onClick={() => abrirAnexo(anexo)}
                    _focus={{ boxShadow: 'none' }}
                  >
                    {anexo.descricao || anexo.nome_original || 'Documento'}
                  </Button>
                ))}
              </Flex>
            </Box>
          )}
        </>
      )}
    </VStack>
  );
}
